import { FormProvider, getFormProps, useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import {
	Box,
	Button,
	DropdownMenu,
	Flex,
	IconButton,
	VisuallyHidden,
} from '@radix-ui/themes'
import { Form, useLocation } from '@remix-run/react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { RxChevronUp } from 'react-icons/rx'

import { z } from 'zod'
import { FormDefaults } from '#app/components/common/form-defaults.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Image } from '#app/components/ui/image'
import { type Locale } from '#app/models/locale'
import { ROOT_INPUTS } from '#app/routes/_layout/route-config.ts'
import { testId } from '#app/utils/client/test-id.ts'
import { schemaDefault } from '#app/utils/form-defaults.ts'

type LocaleData = {
	name: string
	img: {
		filename: string
		alt: string
	}
}

type I18nProps = {
	locale: Locale
}

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	uiI18n = 'ui-i18n',
}

/* -------------------------------------------------------------------------------------------------
 * Inputs
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
	locale = 'locale',
}

/* -------------------------------------------------------------------------------------------------
 * Form
 * -----------------------------------------------------------------------------------------------*/

enum FORM {
	id = 'i18n-form',
	action = '/actions/set-locale',
	fetcherKey = 'i18n',
	subaction = 'i18n',
}

const schema = z.object({
	...schemaDefault,
})

/* -------------------------------------------------------------------------------------------------
 * TEST_ID
 * -----------------------------------------------------------------------------------------------*/

enum TEST_ID {
	i18n = 'i18n',
	popoverTrigger = 'i18n-trigger',
}

const localesMap: Record<string, LocaleData> = {
	de: {
		name: 'Deutsch',
		img: {
			filename: 'de.svg',
			alt: 'German Flag',
		},
	},
	en: {
		name: 'English',
		img: {
			filename: 'us.svg',
			alt: 'US Flag',
		},
	},
	fr: {
		name: 'Français',
		img: {
			filename: 'fr.svg',
			alt: 'Frensh Flag',
		},
	},
	es: {
		name: 'Español',
		img: {
			filename: 'es-variant.svg',
			alt: 'Spain Flag',
		},
	},
	it: {
		name: 'Italiano',
		img: {
			filename: 'it.svg',
			alt: 'Italy Flag',
		},
	},
	el: {
		name: 'Ελληνικά',
		img: {
			filename: 'gr.svg',
			alt: 'Greek Flag',
		},
	},
	pt: {
		name: 'Português',
		img: {
			filename: 'pt.svg',
			alt: 'Portogese Flag',
		},
	},
	ru: {
		name: 'Русский',
		img: {
			filename: 'ru.svg',
			alt: 'Rusia Flag',
		},
	},
	tr: {
		name: 'Türkçe',
		img: {
			filename: 'tr.svg',
			alt: 'Turki Flag',
		},
	},
	zh: {
		name: '中文',
		img: {
			filename: 'cn.svg',
			alt: 'China Flag',
		},
	},
	nl: {
		name: 'Nederlands',
		img: {
			filename: 'nl.svg',
			alt: 'Netherland Flag',
		},
	},
}

/* -------------------------------------------------------------------------------------------------
 * I18n
 * -----------------------------------------------------------------------------------------------*/

const I18n: React.FC<I18nProps> = ({ locale }) => {
	const location = useLocation()
	const { t } = useTranslation(I18NEXT.uiI18n)

	const formValues = {
		[ROOT_INPUTS.subaction]: FORM.subaction,
		[ROOT_INPUTS.noJs]: 'true',
		[ROOT_INPUTS.redirect]: `${location.pathname}${location.search}`,
	}
	const [form, fields] = useForm({
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: schema })
		},
		shouldValidate: 'onInput',
	})

	return (
		<DropdownMenu.Root>
			<DropdownMenu.Trigger className="group">
				<Flex asChild gapX="1">
					<IconButton
						{...testId(TEST_ID.popoverTrigger)}
						variant="ghost"
						size="3"
					>
						<VisuallyHidden>{t('button.trigger')}</VisuallyHidden>
						<Image
							api={false}
							src={`/pwa/flags/${localesMap[locale].img.filename}`}
							alt={localesMap[locale].img.filename}
							width={23}
							height={23}
						/>
						<Icon>
							<RxChevronUp
								size="1em"
								className="transition-all duration-300 ease-in-out group-radix-state-open:rotate-180"
							/>
						</Icon>
					</IconButton>
				</Flex>
			</DropdownMenu.Trigger>
			<DropdownMenu.Content side="top">
				<FormProvider context={form.context}>
					<Form
						method="post"
						action={FORM.action}
						navigate={false}
						fetcherKey={FORM.fetcherKey}
						{...getFormProps(form)}
						preventScrollReset
					>
						<FormDefaults form={form} fields={fields} values={formValues} />
						<ul>
							{Object.entries(localesMap).map(([key, item]) => (
								<DropdownMenu.Item key={key} asChild>
									<li>
										<Box asChild py="3">
											<Button
												variant="ghost"
												name={INPUTS.locale}
												value={key}
												disabled={locale === key}
											>
												<Image
													api={false}
													src={`/pwa/flags/${item.img.filename}`}
													alt={item.img.filename}
													width={23}
													height={23}
													loading="lazy"
												/>
												{item.name}
											</Button>
										</Box>
									</li>
								</DropdownMenu.Item>
							))}
						</ul>
					</Form>
				</FormProvider>
			</DropdownMenu.Content>
		</DropdownMenu.Root>
	)
}
I18n.displayName = 'I18n'

/* -----------------------------------------------------------------------------------------------*/

export {
	I18n,
	localesMap,
	FORM as I18N_FORM,
	INPUTS as I18N_INPUTS,
	I18NEXT as I18N_I18NEXT,
	TEST_ID as I18N_TEST_ID,
	type LocaleData,
	schema as I18nSchema,
}
